
import { NavLink } from 'react-router-dom';
import {
    Store,
    ShoppingBag,
    Users,
    Settings,
    LayoutDashboard,
    Bell,
    CreditCard,
    AlertTriangle,
    BarChart2,
    UserCog,
    Calculator,
    ExternalLink,
    X,
    Target,
} from 'lucide-react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { systemService } from '../../services/system.service';
import { crmService } from '../../services/crm.service';
import { handoffToken } from '../../utils/tokenHandoff';
import { authStorage } from '../../utils/authStorage';
import { PRODUCT_NAME } from '../../config/branding';

// Navigation items definition moved inside component to use hook or defined outside with keys
// Defined outside with translation keys
const platformNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Partners', href: '/partners', icon: Users, superAdminOnly: true },
    { name: 'System Users', href: '/users', icon: Users, superAdminOnly: true },
    { name: 'Settings', href: '/system', icon: Settings, superAdminOnly: true },
];

const superAdminNavigation = [
    { name: 'Plans', href: '/plans', icon: CreditCard },
    { name: 'Paramètres Plateforme', href: '/settings/platform', icon: Settings },
    { name: 'Analytiques', href: '/system/analytics', icon: BarChart2 },
    { name: 'Push Notifications', href: '/push-notifications', icon: Bell },
    { name: 'Comptables', href: '/accountants', icon: Calculator },
];

const restaurantNavigation = [
    { name: 'Restaurants', href: '/restaurants', icon: Store },
    { name: 'Orders', href: '/orders', icon: ShoppingBag, badgeKey: 'orders', superAdminOnly: true },
];

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { serviceNotifications } = useSocket();
    const [incidentCount, setIncidentCount] = useState(0);
    const [newLeadsCount, setNewLeadsCount] = useState(0);

    const activeServiceCount = serviceNotifications.length;
    const isSuperAdmin = user?.role === 'super_admin';
    const isPartner = user?.role === 'partner';
    const isAccountant = user?.role === 'accountant';

    const hasFeature = (item: any) =>
        !item.requiredFeature || user?.plan_features?.includes(item.requiredFeature);
    const visiblePlatformNav = platformNavigation.filter(item => (!item.superAdminOnly || isSuperAdmin) && hasFeature(item));
    const visibleRestaurantNav = restaurantNavigation.filter(item => (!item.superAdminOnly || isSuperAdmin) && hasFeature(item));

    useEffect(() => {
        if (user?.role !== 'super_admin') return;
        systemService.getIncidents()
            .then(res => setIncidentCount(res.data.length))
            .catch(() => {});
        crmService.getNewCount()
            .then(res => setNewLeadsCount(res.data.data.count))
            .catch(() => {});
    }, [user?.role]);

    return (
        <div className={`flex flex-col w-64 bg-gray-900 border-r border-gray-800 h-screen fixed left-0 top-0 text-white overflow-y-auto z-30 transform transition-transform duration-200 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
            <div className="flex items-center justify-between h-16 border-b border-gray-800 flex-shrink-0 px-4">
                <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-500">
                    {PRODUCT_NAME} Admin
                </span>
                <button
                    onClick={onClose}
                    className="md:hidden text-gray-400 hover:text-white p-1 rounded"
                    aria-label="Fermer le menu"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>

            <nav className="flex-1 px-4 py-6 space-y-8">
                {/* Platform Section - Admin, Partner, and Accountant */}
                {(isSuperAdmin || isPartner || isAccountant) && (
                    <div>
                        <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Platform Partner
                        </h3>
                        <div className="mt-2 space-y-1">
                            {visiblePlatformNav.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.href}
                                    className={({ isActive }) =>
                                        clsx(
                                            isActive
                                                ? 'bg-gray-800 text-white'
                                                : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                            'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors'
                                        )
                                    }
                                >
                                    <item.icon
                                        className="mr-3 flex-shrink-0 h-5 w-5"
                                        aria-hidden="true"
                                    />
                                    {t(item.name) !== item.name ? t(item.name) : item.name}
                                </NavLink>
                            ))}
                        </div>

                        {isPartner && (
                            <div className="mt-4 pt-4 border-t border-gray-700">
                                <h4 className="px-2 text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                                    Mon Espace
                                </h4>
                                <div className="mt-1 space-y-1">
                                    {[
                                        { name: 'Prospection', href: '/partner/prospecting', icon: Target },
                                        { name: 'Mon Profil', href: '/partner/profile', icon: UserCog },
                                        { name: 'Mes Managers', href: '/partner/managers', icon: Users },
                                    ].map(item => (
                                        <NavLink
                                            key={item.href}
                                            to={item.href}
                                            className={({ isActive }) =>
                                                clsx(
                                                    isActive
                                                        ? 'bg-gray-800 text-white'
                                                        : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                                    'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors'
                                                )
                                            }
                                        >
                                            <item.icon className="mr-3 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                                            {item.name}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Super Admin Section */}
                {isSuperAdmin && (
                    <div>
                        <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Super Admin
                        </h3>
                        <div className="mt-2 space-y-1">
                            {superAdminNavigation.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.href}
                                    className={({ isActive }) =>
                                        clsx(
                                            isActive
                                                ? 'bg-gray-800 text-white'
                                                : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                            'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors'
                                        )
                                    }
                                >
                                    <item.icon className="mr-3 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                                    {item.name}
                                </NavLink>
                            ))}
                            <NavLink
                                to="/incidents"
                                className={({ isActive }) =>
                                    clsx(
                                        isActive
                                            ? 'bg-gray-800 text-white'
                                            : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors'
                                    )
                                }
                            >
                                <AlertTriangle className="mr-3 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                                <span className="flex-1">Incidents</span>
                                {incidentCount > 0 && (
                                    <span className="ml-auto bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                                        {incidentCount}
                                    </span>
                                )}
                            </NavLink>
                            <NavLink
                                to="/crm"
                                className={({ isActive }) =>
                                    clsx(
                                        isActive
                                            ? 'bg-gray-800 text-white'
                                            : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors'
                                    )
                                }
                                onClick={() => setNewLeadsCount(0)}
                            >
                                <Target className="mr-3 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                                <span className="flex-1">CRM Leads</span>
                                {newLeadsCount > 0 && (
                                    <span className="ml-auto bg-indigo-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                                        {newLeadsCount}
                                    </span>
                                )}
                            </NavLink>
                        </div>
                    </div>
                )}

                {/* Portail Comptable — super_admin et comptable uniquement */}
                {(isSuperAdmin || isAccountant) && (
                    <div>
                        <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Comptabilité
                        </h3>
                        <div className="mt-2 space-y-1">
                            <button
                                onClick={() => {
                                    const token = authStorage.getToken();
                                    const base = (import.meta.env.VITE_ACCOUNTANT_PORTAL_URL as string | undefined) ?? 'http://localhost:3002';
                                    handoffToken(base, token);
                                }}
                                className="group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors w-full text-gray-400 hover:bg-gray-800 hover:text-white"
                            >
                                <Calculator className="mr-3 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                                <span className="flex-1 text-left">Portail Comptable</span>
                                <ExternalLink className="h-3.5 w-3.5 text-gray-600 group-hover:text-gray-400" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Restaurant Section */}
                <div>
                    <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center justify-between">
                        <span>Restaurant Operations</span>
                        {activeServiceCount > 0 && (
                            <span className="bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
                                <Bell size={10} />
                                {activeServiceCount}
                            </span>
                        )}
                    </h3>
                    <div className="mt-2 space-y-1">
                        {visibleRestaurantNav.map((item) => (
                            <NavLink
                                key={item.name}
                                to={item.href}
                                className={({ isActive }) =>
                                    clsx(
                                        isActive
                                            ? 'bg-orange-900/50 text-orange-200'
                                            : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors'
                                    )
                                }
                            >
                                <item.icon
                                    className="mr-3 flex-shrink-0 h-5 w-5"
                                    aria-hidden="true"
                                />
                                <span className="flex-1">{t(item.name) !== item.name ? t(item.name) : item.name}</span>
                                {item.badgeKey === 'orders' && activeServiceCount > 0 && (
                                    <span className="ml-auto bg-orange-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                        {activeServiceCount}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </div>
                </div>
            </nav>
        </div>
    );
}
